import React from "react"
import { LoadingSpinner } from "@/components/LoadingSpinner"
import CallService, { getCallSvcs } from "@/lib/calls"
import { useCallback, useContext, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { VibratoClientContext } from "../contexts"
import { formatAnyPhoneNumber } from "@/lib/phone"
import { MultiplePages } from "iconoir-react"

const navButtons = new Map([
  ["calls-nav-all", { id: "calls-nav-all", name: "All", status: "all" }],
  ["calls-nav-in-progress", { id: "calls-nav-in-progress", name: "In-progress", status: "in_progress" }],
  ["calls-nav-successful", { id: "calls-nav-successful", name: "Successful", status: "successful" }],
  ["calls-nav-scheduled", { id: "calls-nav-scheduled", name: "Scheduled", status: "scheduled" }],
  ["calls-nav-review", { id: "calls-nav-review", name: "Needs Review", status: "review" }]
])

// Format call duration seconds to readable time
const formatDuration = (seconds: number): string => {
  if (!seconds) return "0 sec"

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.round(seconds % 60)

  if (minutes === 0) {
    return `${remainingSeconds} sec${remainingSeconds !== 1 ? "s" : ""}`
  }

  if (remainingSeconds === 0) {
    return `${minutes} min${minutes !== 1 ? "s" : ""}`
  }

  return `${minutes} min${minutes !== 1 ? "s" : ""} ${remainingSeconds} sec${remainingSeconds !== 1 ? "s" : ""}`
}

function CallsNav({
  setFilterStatus,
  activeFilter
}: {
  setFilterStatus: (status: string) => void
  activeFilter: string
}) {
  const clickButton = useCallback(
    (e: any) => {
      e.preventDefault()
      const buttonId = e.target.id
      setFilterStatus(navButtons.get(buttonId)?.status || "all")
    },
    [setFilterStatus]
  )

  return (
    <div className="flex justify-center w-full">
      <div className="flex flex-wrap justify-center gap-3 max-w-full">
        {Array.from(navButtons.values()).map((b) => (
          <button
            key={b.id}
            id={b.id}
            onClick={clickButton}
            className={`px-5 py-2 text-sm rounded-full transition-all duration-200 ${
              b.id === activeFilter
                ? "bg-purple-600 text-white font-medium border border-purple-600 shadow-md"
                : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 shadow-sm hover:shadow"
            }`}
          >
            {b.name}
          </button>
        ))}
      </div>
    </div>
  )
}

export default function CallHistory(): React.JSX.Element {
  const [filterStatus, setFilterStatus] = useState("all")
  const [activeFilter, setActiveFilter] = useState("calls-nav-all")

  useEffect(() => {
    // Update active filter when filter status changes
    for (const [buttonId, buttonInfo] of navButtons.entries()) {
      if (buttonInfo.status === filterStatus) {
        setActiveFilter(buttonId)
        break
      }
    }
  }, [filterStatus])

  return (
    <div className="h-full w-full pt-6 px-6 md:pt-8 md:px-8 flex flex-col gap-6" id="call-history-container">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-100 rounded-xl">
            <MultiplePages width="24" height="24" className="text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Call History</h1>
            <p className="text-gray-600">Review and manage your recent phone calls</p>
          </div>
        </div>
      </div>

      <CallsNav setFilterStatus={setFilterStatus} activeFilter={activeFilter} />

      <div className="flex flex-col grow items-center justify-center">
        <CallTable filterStatus={filterStatus} />
      </div>
    </div>
  )
}

export function CallTable({
  campaignUuid,
  contactUuid,
  filterStatus = "all"
}: {
  campaignUuid?: string
  contactUuid?: string
  filterStatus?: string
}) {
  const vibratoClient = useContext(VibratoClientContext)

  const [tasks, setTasks] = useState<any | null>(null)
  const [allCalls, setAllCalls] = useState<Array<any> | null>(null)
  const [filteredCalls, setFilteredCalls] = useState<Array<any> | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalFilteredCalls, setTotalFilteredCalls] = useState(0)
  const [perPage, setPerPage] = useState(25)

  useEffect(() => {
    document.title = "Vibrato - Call History"
  }, [])

  useEffect(() => {
    async function loadTasks(): Promise<void> {
      if (vibratoClient == null) {
        return
      }

      try {
        const tasks = await vibratoClient.getCallTasks()
        setTasks(tasks)
      } catch (error) {
        console.error("Error loading tasks:", error)
      }
    }

    void loadTasks()
  }, [vibratoClient])

  // Fetch all calls without any pagination
  useEffect(() => {
    async function fetchAllCalls() {
      if (vibratoClient == null) {
        return
      }

      setIsLoading(true)

      try {
        console.log("Fetching all calls...")
        const query: any = {
          pageSize: 1000, // Using a large number to get all calls at once
          status: "active"
        }

        if (campaignUuid) {
          query["campaignUuid"] = campaignUuid
        }
        if (contactUuid) {
          query["contactUuid"] = contactUuid
        }

        const response = await vibratoClient.getCalls(query)
        setAllCalls(response.results)
        console.log(`Retrieved ${response.results.length} total calls`)
      } catch (error) {
        console.error("Error fetching all calls:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchAllCalls()
  }, [vibratoClient, campaignUuid, contactUuid])

  // Apply client-side filtering and pagination when calls or filter status changes
  useEffect(() => {
    if (allCalls) {
      // Group calls by status
      const callSvcsByStatus = getCallSvcs(allCalls)

      // Apply filtering based on status
      const filtered = callSvcsByStatus[filterStatus] || []
      setTotalFilteredCalls(filtered.length)

      // Convert back to raw call objects for consistency
      const filteredCallObjects = filtered.map((svc: CallService) => svc.getCall())
      setFilteredCalls(filteredCallObjects)

      // Reset to page 1 when changing filters
      setCurrentPage(1)

      console.log(`Filtered to ${filteredCallObjects.length} ${filterStatus} calls`)
    }
  }, [allCalls, filterStatus])

  // Get the current page of data based on pagination settings
  const indexOfLastCall = currentPage * perPage
  const indexOfFirstCall = indexOfLastCall - perPage
  const currentCalls = filteredCalls ? filteredCalls.slice(indexOfFirstCall, indexOfLastCall) : []
  const totalPages = Math.ceil(totalFilteredCalls / perPage)

  const changePage = useCallback(
    (delta: number) => {
      return (e: any): void => {
        e.preventDefault()
        e.stopPropagation()

        let page: number = currentPage + delta
        if (page < 1) {
          page = 1
        }
        if (page > totalPages) {
          page = totalPages
        }

        setCurrentPage(page)
      }
    },
    [currentPage, totalPages]
  )

  const handlePerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPerPage = parseInt(e.target.value, 10)
    setPerPage(newPerPage)
    setCurrentPage(1) // Reset to first page when changing results per page
  }

  // Calculate pagination values
  const currentResultStart = totalFilteredCalls === 0 ? 0 : indexOfFirstCall + 1
  const currentResultEnd = Math.min(indexOfLastCall, totalFilteredCalls)

  if (isLoading || tasks == null || allCalls == null || filteredCalls == null) {
    return (
      <div className="flex flex-col items-center justify-center min-h-fit">
        <div className="flex flex-col items-center space-y-4">
          <LoadingSpinner className="w-10 h-10 text-purple-600" />
          <p className="text-gray-500 font-medium">Loading calls...</p>
        </div>
      </div>
    )
  }

  if (filteredCalls.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center min-h-fit">
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="text-gray-500 text-xl mb-2">No calls found</div>
          <p className="text-gray-400 text-sm">Try adjusting your filters or check back later.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full grow flex flex-col space-y-4 pb-4">
      {/* Desktop View - Hidden on mobile */}
      <div className="w-full grow-0 md:grow hidden md:flex flex-col bg-white shadow-md rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3">
                  Call
                </th>
                <th scope="col" className="px-4 py-3">
                  Date
                </th>
                <th scope="col" className="px-4 py-3">
                  Status
                </th>
                <th scope="col" className="px-4 py-3">
                  Duration
                </th>
                <th scope="col" className="px-4 py-3">
                  Next steps
                </th>
              </tr>
            </thead>
            <tbody>
              {currentCalls
                .map((call) => new CallService(call))
                .map((svc) => (
                  <tr key={svc.getCall().id} className="border-b border-gray-200 hover:bg-gray-50">
                    <th scope="row" className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                      <Link to={`/calls/${svc.getCall().id}`}>
                        <span className="text-purple-600 hover:text-purple-700 block">{svc.getFrontendName()}</span>
                        {svc.getCall().task_recipient_slug === "anyone" && (
                          <span className="text-xs text-gray-500 block mt-1">
                            {formatAnyPhoneNumber(svc.getCall().country_code, svc.getCall().phone_number)}
                          </span>
                        )}
                      </Link>
                    </th>
                    <td className="px-4 py-3">{svc.dateStarted()}</td>
                    <td className="px-4 py-3">{svc.getStatusForDisplay()}</td>
                    <td className="px-4 py-3">{formatDuration(svc.getDuration())}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap">
                        {svc.isInProgress() && (
                          <span className="bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full w-max flex items-center animate-pulse">
                            <span className="h-2 w-2 rounded-full bg-blue-600 mr-1.5" />
                            Calling
                          </span>
                        )}
                        {svc.succeeded() && svc.followUps().length == 0 && (
                          <span className="bg-green-100 text-green-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full w-max">
                            Finished
                          </span>
                        )}
                        {svc.followUps().includes("missing_data") && (
                          <span className="bg-amber-100 text-amber-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full w-max">
                            Needs review
                          </span>
                        )}
                        {svc.followUps().includes("needs_action") && (
                          <span className="bg-yellow-100 text-yellow-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full w-max">
                            Needs action
                          </span>
                        )}
                        {!svc.succeeded() && svc.followUps().length == 0 && !svc.isInProgress() && (
                          <span className="bg-amber-100 text-amber-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full w-max">
                            Needs review
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card View - Hidden on desktop */}
      <div className="grow md:grow-0 md:hidden space-y-3">
        {currentCalls
          .map((call) => new CallService(call))
          .map((svc) => (
            <CallMobileCard key={svc.getCall().id} svc={svc} />
          ))}
      </div>

      {/* Pagination */}
      <nav
        className="flex justify-between items-center p-4 bg-white md:bg-gray-50 rounded-xl shadow-sm border-t md:border border-gray-200"
        aria-label="Table navigation"
      >
        <span className="text-sm text-gray-500 flex items-center gap-3">
          <span className="hidden md:inline">Showing </span>
          <span className="font-semibold text-gray-900">
            {currentResultStart}-{currentResultEnd}
          </span>
          <span className="hidden md:inline"> of </span>
          <span className="md:hidden"> / </span>
          <span className="font-semibold text-gray-900">{totalFilteredCalls}</span>

          <span className="hidden md:flex items-center gap-2 ml-2 md:ml-4">
            <label htmlFor="perPage" className="text-xs md:text-sm text-gray-500">
              Results per page:
            </label>
            <select
              id="perPage"
              value={perPage}
              onChange={handlePerPageChange}
              className="min-w-[80px] bg-white border border-gray-300 text-gray-700 text-xs rounded-lg p-1 md:p-1.5 focus:ring-purple-500 focus:border-purple-500"
              aria-label="Results per page"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </span>
        </span>

        <div className="flex items-center gap-2">
          <button
            disabled={currentPage === 1}
            onClick={changePage(-1)}
            className={`flex items-center justify-center gap-1 px-3 h-10 ${
              currentPage === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100"
            } rounded-full shadow-sm border border-gray-200`}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm">Previous</span>
          </button>

          <div className="hidden md:block text-sm font-medium text-gray-700">
            Page {currentPage} of {totalPages || 1}
          </div>

          <button
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={changePage(1)}
            className={`flex items-center justify-center gap-1 px-3 h-10 ${
              currentPage === totalPages || totalPages === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100"
            } rounded-full shadow-sm border border-gray-200`}
          >
            <span className="text-sm">Next</span>
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </nav>
    </div>
  )
}

// Extracted Mobile Card Component
export function CallMobileCard({ svc }: { svc: CallService }): React.JSX.Element {
  return (
    <Link key={svc.getCall().id} to={`/calls/${svc.getCall().id}`} className="block">
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 hover:shadow-md transition-all duration-200">
        <div className="flex justify-between items-start mb-3">
          <div className="text-purple-600 font-medium text-base line-clamp-2 leading-snug pr-2">
            {svc.getFrontendName()}
          </div>
          <div className="text-xs text-gray-500 whitespace-nowrap">{svc.dateStarted()}</div>
        </div>

        {/* Wrapping Row for Call Details */}
        <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
          {/* Recipient Info */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">To:</span>
            {formatAnyPhoneNumber(svc.getCall().country_code, svc.getCall().phone_number)}
          </div>

          {/* Status */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Status:</span>
            <span className="text-gray-700 font-medium">{svc.getStatusForDisplay()}</span>
          </div>

          {/* Duration */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Duration:</span>
            <span className="text-gray-700 font-medium">{formatDuration(svc.getDuration())}</span>
          </div>
        </div>

        {/* Next Steps - Kept separate for better layout flow */}
        <div className="mt-3 flex items-start gap-1.5">
          <span className="text-xs text-gray-500 shrink-0 pt-0.5 w-fit">Next steps:</span>
          <div className="flex flex-wrap gap-1">
            {svc.isInProgress() && (
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full w-max flex items-center animate-pulse">
                <span className="h-2 w-2 rounded-full bg-blue-600 mr-1.5" />
                Calling
              </span>
            )}
            {svc.succeeded() && svc.followUps().length == 0 && (
              <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full w-max">
                Finished
              </span>
            )}
            {svc.followUps().includes("missing_data") && (
              <span className="bg-amber-100 text-amber-800 text-xs font-medium px-2.5 py-0.5 rounded-full w-max">
                Needs review
              </span>
            )}
            {svc.followUps().includes("needs_action") && (
              <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded-full w-max">
                Needs action
              </span>
            )}
            {!svc.succeeded() && svc.followUps().length == 0 && !svc.isInProgress() && (
              <span className="bg-amber-100 text-amber-800 text-xs font-medium px-2.5 py-0.5 rounded-full w-max">
                Needs review
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  )
}
